// Layout Components
// AppBar, Bottom Navigation, Drawer, Search Modal

import * as React from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
  styled,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  NotificationsNone as NotificationsIcon,
  AccountCircle as AccountIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  ChevronLeft,
  Close,
  Home,
  Movie,
  CalendarMonth,
  Bookmark,
  Notifications,
  Person,
  Settings,
  Logout,
  BrightnessAuto,
  History,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { useSettings } from '../../hooks/useSettings';
import { SearchModal } from './SearchModal';
import { Button } from '../ui/Button';

const useStyles = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  return { isMobile };
};

// Styled Components
const StyledAppBar = styled(MuiAppBar)(({ theme }) => ({
  backgroundColor: 'var(--anime-token-background-backdrop)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderBottom: `1px solid var(--anime-token-divider)`,
  zIndex: 'var(--anime-token-zIndex-appBar)',
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '1.5rem',
  letterSpacing: '-0.5px',
  background: 'var(--anime-gradient-text-accent)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textDecoration: 'none',
  '&:hover': {
    opacity: 0.9,
  },
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  color: 'var(--anime-token-text-secondary)',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: 'var(--anime-token-text-primary)',
    backgroundColor: 'var(--anime-token-surface-hover)',
  },
  '&.active': {
    color: 'var(--anime-token-brand-primary-main)',
  },
}));

const SearchButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(0.5),
  color: 'var(--anime-token-text-secondary)',
  '&:hover': {
    backgroundColor: 'var(--anime-token-surface-hover)',
    color: 'var(--anime-token-text-primary)',
  },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  fontSize: '0.875rem',
  fontWeight: 600,
  cursor: 'pointer',
  border: `2px solid transparent`,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'var(--anime-token-brand-primary-main)',
    transform: 'scale(1.05)',
  },
}));

const MobileNav = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 'var(--anime-token-zIndex-bottomNav)',
  backgroundColor: 'var(--anime-token-background-backdrop)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderTop: `1px solid var(--anime-token-divider)`,
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  paddingBottom: `calc(${theme.spacing(1)} + env(safe-area-inset-bottom, 0px))`,
}));

const NavItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: active ? 'var(--anime-token-brand-primary-main)' : 'var(--anime-token-text-tertiary)',
  fontSize: '0.7rem',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  flex: 1,
  minHeight: 56,
  '&:hover': {
    color: active ? 'var(--anime-token-brand-primary-main)' : 'var(--anime-token-text-secondary)',
    backgroundColor: 'var(--anime-token-surface-hover)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

// Main AppBar Component
export const AppBar = () => {
  const theme = useTheme();
  const { isMobile } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { themeMode, toggleTheme } = useSettings();
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState<HTMLElement | null>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    handleUserMenuClose();
  };

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { path: '/home', label: 'Home', icon: <Home fontSize="large" /> },
    { path: '/movies', label: 'Movies', icon: <Movie fontSize="large" /> },
    { path: '/schedule', label: 'Schedule', icon: <CalendarMonth fontSize="large" /> },
    { path: '/watchlist', label: 'Watchlist', icon: <Bookmark fontSize="large" /> },
  ];

  return (
    <>
      <StyledAppBar position="fixed">
        <Toolbar variant="dense" disableGutters={!isMobile}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Open menu"
              onClick={handleMobileMenuOpen}
              sx={{ mr: 1 }}
            >
              <MenuIcon fontSize="large" />
            </IconButton>
          )}

          {/* Logo */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Logo component="a" href="/" sx={{ mr: isMobile ? 1 : 2, whiteSpace: 'nowrap' }}>
              AnimeFlow
            </Logo>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {navItems.map((item) => (
                  <Tooltip key={item.path} title={item.label} arrow>
                    <NavButton
                      active={isActive(item.path)}
                      onClick={() => navigate(item.path)}
                      aria-current={isActive(item.path) ? 'page' : undefined}
                    >
                      {item.icon}
                    </NavButton>
                  </Tooltip>
                ))}
              </Box>
            )}

            {/* Search */}
            <SearchButton
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              <SearchIcon fontSize="large" />
            </SearchButton>
          </Box>

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Theme Toggle */}
            <Tooltip title={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} arrow>
              <IconButton
                color="inherit"
                onClick={toggleTheme}
                aria-label={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {themeMode === 'dark' ? <LightModeIcon fontSize="large" /> : <DarkModeIcon fontSize="large" />}
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            <Tooltip title="Notifications" arrow>
              <IconButton color="inherit" onClick={() => navigate('/notifications')}>
                <Badge badgeContent={3} color="error" overlap="circular">
                  <NotificationsIcon fontSize="large" />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Menu / Login */}
            {user ? (
              <Tooltip title={user.displayName || user.email || 'Account'} arrow>
                <UserAvatar
                  alt={user.displayName || user.email || 'User'}
                  src={user.photoURL || undefined}
                  onClick={handleUserMenuOpen}
                />
              </Tooltip>
            ) : (
              <Button
                variant="ghost"
                size="small"
                onClick={() => navigate('/login')}
                sx={{ mr: 1, display: { xs: 'none', md: 'inline-flex' } }}
              >
                Sign In
              </Button>
            )}

            {/* Mobile Menu Button */}
            {isMobile && !user && (
              <IconButton
                color="inherit"
                onClick={handleMobileMenuOpen}
                aria-label="Open menu"
              >
                <MenuIcon fontSize="large" />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </StyledAppBar>

      {/* User Dropdown Menu */}
      <AnimatePresence>
        {userMenuAnchor && (
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                borderRadius: 2,
                mt: 1,
                minWidth: 220,
                backgroundColor: 'var(--anime-token-background-elevated)',
                border: '1px solid var(--anime-token-divider)',
                boxShadow: 'var(--anime-token-shadow-xl)',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--anime-token-divider)' }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {user.displayName || user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
            <Divider />
            <List>
              <ListItem
                button
                onClick={() => { navigate('/profile'); handleUserMenuClose(); }}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemIcon sx={{ color: 'var(--anime-token-text-secondary)' }}>
                  <Person fontSize="inherit" />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </ListItem>
              <ListItem
                button
                onClick={() => { navigate('/watchlist'); handleUserMenuClose(); }}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemIcon sx={{ color: 'var(--anime-token-text-secondary)' }}>
                  <Bookmark fontSize="inherit" />
                </ListItemIcon>
                <ListItemText primary="Watchlist" />
              </ListItem>
              <ListItem
                button
                onClick={() => { navigate('/history'); handleUserMenuClose(); }}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemIcon sx={{ color: 'var(--anime-token-text-secondary)' }}>
                  <History fontSize="inherit" />
                </ListItemIcon>
                <ListItemText primary="History" />
              </ListItem>
              <Divider />
              <ListItem
                button
                onClick={() => { navigate('/settings'); handleUserMenuClose(); }}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemIcon sx={{ color: 'var(--anime-token-text-secondary)' }}>
                  <Settings fontSize="inherit" />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </ListItem>
            </List>
            <Divider />
            <List>
              <ListItem
                button
                onClick={handleLogout}
                sx={{ py: 1.5, px: 2, color: 'var(--anime-token-palette-error-main)' }}
              >
                <ListItemIcon sx={{ color: 'var(--anime-token-palette-error-main)' }}>
                  <Logout fontSize="inherit" />
                </ListItemIcon>
                <ListItemText primary="Sign Out" primaryTypographyProps={{ color: 'var(--anime-token-palette-error-main)' }} />
              </ListItem>
            </List>
          </Menu>
        )}
      </AnimatePresence>

      {/* Mobile Drawer Menu */}
      <AnimatePresence>
        {mobileMenuAnchor && (
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                borderRadius: 2,
                mt: 1,
                minWidth: 280,
                backgroundColor: 'var(--anime-token-background-elevated)',
                border: '1px solid var(--anime-token-divider)',
                boxShadow: 'var(--anime-token-shadow-xl)',
              },
            }}
          >
            <Box sx={{ px: 2, py: 2, borderBottom: '1px solid var(--anime-token-divider)' }}>
              <Logo component="span">AnimeFlow</Logo>
            </Box>
            <List>
              {navItems.map((item) => (
                <ListItem
                  key={item.path}
                  button
                  onClick={() => { navigate(item.path); handleMobileMenuClose(); }}
                  selected={isActive(item.path)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: 1.5,
                    mx: 1,
                    my: 0.5,
                    '&.Mui-selected': {
                      backgroundColor: 'var(--anime-token-brand-primary-500_15)',
                      color: 'var(--anime-token-brand-primary-main)',
                      '&:hover': {
                        backgroundColor: 'var(--anime-token-brand-primary-500_25)',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: isActive(item.path) ? 'var(--anime-token-brand-primary-main)' : 'var(--anime-token-text-secondary)' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItem>
              ))}
            </List>
            <Divider />
            {user ? (
              <>
                <List>
                  <ListItem button onClick={() => { navigate('/profile'); handleMobileMenuClose(); }} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: 'var(--anime-token-text-secondary)' }}><Person fontSize="inherit" /></ListItemIcon>
                    <ListItemText primary="Profile" />
                  </ListItem>
                  <ListItem button onClick={() => { navigate('/watchlist'); handleMobileMenuClose(); }} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: 'var(--anime-token-text-secondary)' }}><Bookmark fontSize="inherit" /></ListItemIcon>
                    <ListItemText primary="Watchlist" />
                  </ListItem>
                  <ListItem button onClick={() => { navigate('/settings'); handleMobileMenuClose(); }} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ color: 'var(--anime-token-text-secondary)' }}><Settings fontSize="inherit" /></ListItemIcon>
                    <ListItemText primary="Settings" />
                  </ListItem>
                  <ListItem button onClick={handleLogout} sx={{ py: 1.5, px: 2, color: 'var(--anime-token-palette-error-main)' }}>
                    <ListItemIcon sx={{ color: 'var(--anime-token-palette-error-main)' }}><Logout fontSize="inherit" /></ListItemIcon>
                    <ListItemText primary="Sign Out" />
                  </ListItem>
                </List>
              </>
            ) : (
              <Box sx={{ p: 2 }}>
                <Button fullWidth variant="contained" onClick={() => { navigate('/login'); handleMobileMenuClose(); }}>
                  Sign In
                </Button>
              </Box>
            )}
          </Menu>
        )}
      </AnimatePresence>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <MobileNav>
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              active={isActive(item.path)}
              onClick={() => navigate(item.path)}
            >
              {item.icon}
              <span>{item.label}</span>
            </NavItem>
          ))}
          {/* Floating Search Button */}
          <Box sx={{ position: 'absolute', top: -28, left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}>
            <Button
              variant="gradient"
              size="small"
              startIcon={<SearchIcon fontSize="small" />}
              onClick={() => setSearchOpen(true)}
              sx={{ boxShadow: 'var(--anime-token-shadow-xl)', padding: '8px 16px' }}
            >
              Search
            </Button>
          </Box>
        </MobileNav>
      )}

      {/* Search Modal */}
      <SearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={(id: number) => navigate(`/anime/${id}`)}
      />
    </>
  );
};

// Page Layout Wrapper
export interface PageLayoutProps {
  children: React.ReactNode;
  className?: string;
  fullWidth?: boolean;
}

export const PageLayout = ({ children, className, fullWidth = false }: PageLayoutProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      className={className}
      sx={{
        flexGrow: 1,
        width: '100%',
        maxWidth: fullWidth ? '100%' : '1400px',
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        pt: { xs: isMobile ? 6 : 8, md: 9 },
        pb: 4,
      }}
    >
      {children}
    </Box>
  );
};

// Container for full-width sections (hero, etc)
export const FullWidthContainer = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <Box className={className} sx={{ width: '100%', px: { xs: 2, sm: 3, md: 4 } }}>
    {children}
  </Box>
);

// Section Wrapper
export interface SectionProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  id?: string;
}

export const Section = ({ title, subtitle, action, children, className, id }: SectionProps) => (
  <Box className={className} id={id} sx={{ mb: 5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.5rem', md: '1.75rem' },
            letterSpacing: '-0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

export default AppBar;